
import { Suspense, lazy, useState } from "react";
const RealResults = lazy(() => import("@/components/RealResults"));
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import SocialProofLogos from "@/components/SocialProofLogos";
import SimpleHowItWorks from "@/components/SimpleHowItWorks";
import SEOHead from "@/components/SEOHead";
import LazyLoadSection from "@/components/LazyLoadSection";

// Lazy-load below-the-fold and interactive components to reduce initial JS
const TestimonialsSection = lazy(() => import("@/components/TestimonialsSection"));
const ProblemSolution = lazy(() => import("@/components/ProblemSolution"));
const About = lazy(() => import("@/components/About"));
const DoorDashSavings = lazy(() => import("@/components/DoorDashSavings"));
const FinalCTA = lazy(() => import("@/components/FinalCTA"));
const Footer = lazy(() => import("@/components/Footer"));
const EnhancedFeeCalculator = lazy(() => import("@/components/EnhancedFeeCalculator"));
const MerchantProcessingFAQ = lazy(() => import("@/components/MerchantProcessingFAQ"));
const BlogSection = lazy(() => import("@/components/blog/BlogSection"));
const TrustBadges = lazy(() => import("@/components/TrustBadges"));
const ChatWidget = lazy(() => import("@/components/chat/ChatWidget"));
const ExitIntentPopup = lazy(() => import("@/components/ExitIntentPopup"));
const MobileStickyBar = lazy(() => import("@/components/MobileStickyBar"));
const ApplyNowModal = lazy(() => import("@/components/ApplyNowModal"));
const ComparisonSection = lazy(() => import("@/components/ComparisonSection"));
const StickyScrollCTA = lazy(() => import("@/components/StickyScrollCTA"));

const Index = () => {
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);

  return (
    <>
      <SEOHead 
        title="Cash Discount Program South Florida | Processing Bros"
        description="Eliminate 95% of credit card fees with our compliant cash discount program. 10,000+ South Florida businesses saved $8,000+ yearly. Call (954) 399-2230."
        image="/processing-bros-icon-ios.png"
        keywords="merchant services, credit card processing, crypto POS systems, Fort Lauderdale, South Florida, payment processing"
      />
      <main id="main-content" className="min-h-screen bg-background"
            itemScope 
            itemType="https://schema.org/WebPage">
        <Header />
        <Hero />
        
        {/* Social Proof Logo Bar */}
        <SocialProofLogos />
        
        {/* Simple 3-Step How It Works */}
        <SimpleHowItWorks />
        
        {/* Credit Card Processing Fee Calculator Section */}
        <LazyLoadSection>
          <Suspense fallback={<div className="min-h-[400px]" />}>
            <section id="fee-calculator" className="py-16 bg-muted/20" aria-labelledby="calculator-heading">
              <div className="container mx-auto px-4">
                <header className="text-center mb-12">
                  <h2 id="calculator-heading" className="text-3xl font-bold mb-4 text-foreground">Calculate Your Credit Card Processing Savings</h2>
                  <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                    Use our advanced processing fee calculator to compare competitors, see monthly/annual projections, and estimate your potential savings.
                  </p>
                </header>
                <EnhancedFeeCalculator />
              </div>
            </section>
          </Suspense>
        </LazyLoadSection>

        {/* Real Results Section */}
        <LazyLoadSection>
          <Suspense fallback={<div className="min-h-[300px]" />}>
            <RealResults />
          </Suspense>
        </LazyLoadSection>

        {/* Customer Testimonials */}
        <LazyLoadSection>
          <Suspense fallback={<div className="min-h-[300px]" />}>
            <TestimonialsSection />
          </Suspense>
        </LazyLoadSection>

        {/* Comparison: Us vs Competitors */}
        <LazyLoadSection>
          <Suspense fallback={<div className="min-h-[300px]" />}>
            <ComparisonSection />
          </Suspense>
        </LazyLoadSection>

        <LazyLoadSection>
          <Suspense fallback={<div className="min-h-[300px]" />}>
            <ProblemSolution />
          </Suspense>
        </LazyLoadSection>

        <LazyLoadSection>
          <Suspense fallback={<div className="min-h-[300px]" />}>
            <About />
          </Suspense>
        </LazyLoadSection>

        <LazyLoadSection>
          <Suspense fallback={<div className="min-h-[200px]" />}>
            <DoorDashSavings />
          </Suspense>
        </LazyLoadSection>

        <LazyLoadSection>
          <Suspense fallback={<div className="min-h-[200px]" />}>
            <BlogSection />
          </Suspense>
        </LazyLoadSection>

        <LazyLoadSection>
          <Suspense fallback={<div className="min-h-[200px]" />}>
            <MerchantProcessingFAQ />
          </Suspense>
        </LazyLoadSection>
        
        {/* Service Area Section */}
        <LazyLoadSection>
          <section className="py-20 bg-muted/30">
            <div className="container mx-auto px-4 text-center">
              <h2 className="text-3xl font-bold mb-4 text-foreground">
                Proudly Serving South Florida &amp; Beyond
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed mb-8">
                Local expertise in Broward, Miami-Dade, and Palm Beach Counties with nationwide merchant services available. Based in Fort Lauderdale, we provide hands-on support to help your business thrive.
              </p>
              <div className="flex flex-wrap justify-center gap-4 text-sm font-medium">
                <span className="px-4 py-2 bg-primary/10 text-primary rounded-full">📍 Broward County</span>
                <span className="px-4 py-2 bg-primary/10 text-primary rounded-full">📍 Miami-Dade County</span>
                <span className="px-4 py-2 bg-primary/10 text-primary rounded-full">📍 Palm Beach County</span>
                <span className="px-4 py-2 bg-accent/10 text-accent-foreground rounded-full">🇺🇸 Nationwide Service</span>
              </div>
            </div>
          </section>
        </LazyLoadSection>

        {/* Trust Badges Section */}
        <LazyLoadSection>
          <Suspense fallback={<div className="min-h-[200px]" />}>
            <TrustBadges />
          </Suspense>
        </LazyLoadSection>

        <Suspense fallback={<div className="min-h-[200px]" />}>
          <FinalCTA />
        </Suspense>
        <Suspense fallback={<div className="min-h-[200px]" />}>
          <Footer />
        </Suspense>
        
        {/* Deferred interactive widgets */}
        <Suspense fallback={null}>
          <StickyScrollCTA />
        </Suspense>
        <Suspense fallback={null}>
          <ChatWidget />
        </Suspense>
        
        <Suspense fallback={null}>
          <ExitIntentPopup />
        </Suspense>
        
        {/* Mobile Sticky CTA Bar */}
        <Suspense fallback={null}>
          <MobileStickyBar onApplyClick={() => setIsApplyModalOpen(true)} />
        </Suspense>
        <Suspense fallback={null}>
          <ApplyNowModal open={isApplyModalOpen} onOpenChange={setIsApplyModalOpen} />
        </Suspense>
      </main>
    </>
  );
};

export default Index;
